import type { RefObject } from "react";
import { useEffect } from "react";

import { FRAGMENT, SHEET, VERTEX } from "./goreTexLogoShaders";

import sheetUrl from "@assets/goretex/logo-distance/logo-sheet.png";
import delays from "@assets/goretex/logo-distance/timing.json";

const ends = delays.map((_, index) => delays.slice(0, index + 1).reduce((sum, delay) => sum + delay, 0));
const duration = ends[ends.length - 1];

interface IOptions {
  containerRef: RefObject<HTMLElement | null>;
  videoRef: RefObject<HTMLVideoElement | null>;
  canvasRef: RefObject<HTMLCanvasElement | null>;
}

export default function useThermalLogo({ containerRef, videoRef, canvasRef }: IOptions) {
  useEffect(() => {
    const container = containerRef.current;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const gl = canvas?.getContext("webgl", { alpha: true, premultipliedAlpha: true });
    if (!container || !video || !canvas || !gl) return;

    const program = gl.createProgram();
    const buffer = gl.createBuffer();
    const texture = gl.createTexture();
    const shaders: WebGLShader[] = [];
    const reduced = matchMedia("(prefers-reduced-motion: reduce)");
    let disposed = false;
    let visible = false;
    let loading = false;
    let loaded = false;
    let animationId = 0;
    let lastFrame = -1;
    let lastLogo = -1;

    const release = () => {
      shaders.forEach((shader) => {
        gl.deleteShader(shader);
      });
      gl.deleteTexture(texture);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
    };
    if (!program || !buffer || !texture) {
      release();
      return;
    }
    for (const [type, source] of [
      [gl.VERTEX_SHADER, VERTEX],
      [gl.FRAGMENT_SHADER, FRAGMENT],
    ] as const) {
      const shader = gl.createShader(type);
      if (!shader) {
        release();
        return;
      }
      shaders.push(shader);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        release();
        return;
      }
      gl.attachShader(program, shader);
    }
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      release();
      return;
    }
    gl.useProgram(program);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const position = gl.getAttribLocation(program, "p");
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    const time = gl.getUniformLocation(program, "time");
    const reducedMotion = gl.getUniformLocation(program, "reducedMotion");
    const cell = gl.getUniformLocation(program, "cell");
    canvas.width = 640;
    canvas.height = 640;
    gl.viewport(0, 0, 640, 640);

    const draw = () => {
      animationId = 0;
      if (disposed || !visible || !loaded || gl.isContextLost()) return;
      const frame = Math.floor(video.currentTime * 24);
      if (frame !== lastFrame) {
        lastFrame = frame;
        const cycle = video.currentTime % duration;
        const found = ends.findIndex((end) => cycle < end);
        // 뒤쪽 절반은 앞쪽 프레임을 거꾸로 재생
        const index = found < 0 ? 0 : Math.min(found, ends.length - 1 - found);
        if (index !== lastLogo) {
          gl.uniform2f(cell, index % SHEET.columns, Math.floor(index / SHEET.columns));
          lastLogo = index;
        }
        gl.uniform1f(time, frame / 24);
        gl.uniform1i(reducedMotion, reduced.matches ? 1 : 0);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      }
      if (!video.paused) animationId = requestAnimationFrame(draw);
    };
    const start = () => {
      if (!animationId) draw();
    };
    const preferenceChanged = () => {
      lastFrame = -1;
      start();
    };
    const contextLost = (event: Event) => {
      event.preventDefault();
      cancelAnimationFrame(animationId);
      animationId = 0;
    };
    const load = async () => {
      if (loading) return;
      loading = true;
      try {
        const image = new Image();
        image.src = sheetUrl;
        await image.decode();
        if (!disposed) {
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, image);
          loaded = true;
          start();
        }
      } catch {
        // 시트를 못 불러오면 로고 없이 표시
      }
    };
    const observer = new IntersectionObserver(([entry]) => {
      visible = Boolean(entry?.isIntersecting);
      if (visible) {
        if (loaded) start();
        else void load();
      } else {
        cancelAnimationFrame(animationId);
        animationId = 0;
      }
    });
    observer.observe(container);
    video.addEventListener("play", start);
    video.addEventListener("seeked", start);
    reduced.addEventListener("change", preferenceChanged);
    canvas.addEventListener("webglcontextlost", contextLost);
    return () => {
      disposed = true;
      observer.disconnect();
      cancelAnimationFrame(animationId);
      video.removeEventListener("play", start);
      video.removeEventListener("seeked", start);
      reduced.removeEventListener("change", preferenceChanged);
      canvas.removeEventListener("webglcontextlost", contextLost);
      release();
    };
  }, [containerRef, videoRef, canvasRef]);
}
